class KootaProductSection {
  constructor(section) {
    this.section = section;
    this.mainImage = section.querySelector('.koota-product__main-image');
    this.quantityInput = section.querySelector('[name="quantity"]');
    this.variantSelect = section.querySelector('select[name="id"]');
    this.productForm = section.querySelector('.koota-product__form');
    this.sectionId = section.dataset.kootaProductSection;
    this.productUrl = section.dataset.productUrl || window.location.pathname;
    this.basePrice = Number.parseInt(section.dataset.basePrice, 10) || 0;
    this.freeShippingThreshold = Number.parseInt(section.dataset.freeShippingThreshold, 10) || 0;
    this.currency = section.dataset.currency || 'EUR';
    this.locale = section.dataset.locale || document.documentElement.lang || 'fr-FR';
    this.planSelect = section.querySelector('[data-koota-selling-plan-select]');
    this.planInput = section.querySelector('[data-koota-selling-plan-input]');
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    this.isSubmitting = false;
    this.currentVariantId = this.variantSelect?.value || section.querySelector('input[name="id"]')?.value || '';
    this.currentVariantAvailable = !section.querySelector('[data-koota-add-button]')?.disabled;
    this.variantRequest = null;
    this.messages = {
      variantWaiting: section.dataset.i18nVariantWaiting || 'Please wait while the variant is updated.',
      variantUpdating: section.dataset.i18nVariantUpdating || 'Updating variant…',
      variantUpdated: section.dataset.i18nVariantUpdated || 'Variant updated.',
      variantError: section.dataset.i18nVariantError || 'This variant could not be updated. Please try again.',
      subscriptionDelivery: section.dataset.i18nSubscriptionDelivery || '__PRICE__ per delivery',
      subscriptionSaving: section.dataset.i18nSubscriptionSaving || 'save __AMOUNT__',
      shippingUnlocked: section.dataset.i18nShippingUnlocked || 'Free shipping with this quantity.',
      shippingRemaining: section.dataset.i18nShippingRemaining || '__AMOUNT__ left to qualify for free shipping.',
    };

    this.bindGallery();
    this.bindVariants();
    this.bindFormState();
    this.bindFormatTransitions();
    this.bindQuantity();
    this.bindPurchaseOptions();
    this.bindQuantityTiers();
    this.bindStickyBuy();
    this.updateCommerceState();
  }

  bindGallery() {
    if (!this.mainImage) return;

    this.section.querySelectorAll('[data-koota-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => {
        const imageUrl = button.dataset.imageUrl;
        if (!imageUrl) return;

        this.mainImage.classList.add('is-changing');
        this.mainImage.src = imageUrl;
        this.mainImage.srcset = '';
        this.mainImage.alt = button.dataset.imageAlt || '';

        this.section.querySelectorAll('[data-koota-thumbnail]').forEach((thumbnail) => {
          const isCurrent = thumbnail === button;
          thumbnail.classList.toggle('is-active', isCurrent);
          thumbnail.setAttribute('aria-pressed', String(isCurrent));
        });

        requestAnimationFrame(() => this.mainImage.classList.remove('is-changing'));
      });
    });
  }

  bindVariants() {
    if (!this.variantSelect || !this.sectionId) return;

    this.variantSelect.addEventListener('change', () => {
      const variantId = this.variantSelect.value;
      if (!variantId || variantId === this.currentVariantId) return;

      this.loadVariant(variantId);
    });
  }

  bindFormState() {
    this.productForm?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.variantRequest) {
      this.announceVariantStatus(this.messages.variantWaiting);
      return;
    }
    if (this.isSubmitting) return;

    const subscriptionOption = this.section.querySelector(
      '[data-koota-purchase-option][value="subscription"]:checked'
    );

    if (subscriptionOption && (!this.planSelect?.value || !this.planInput)) {
      this.announceVariantStatus(this.messages.variantError);
      return;
    }

    this.syncSellingPlanInput(Boolean(subscriptionOption));
    this.setQuantity(this.getQuantity());

    const formData = new FormData(this.productForm);
    this.productForm.querySelectorAll('[name^="koota_purchase_option_"]').forEach((input) => {
      formData.delete(input.name);
    });

    if (this.cart?.getSectionsToRender) {
      formData.set('sections', this.cart.getSectionsToRender().map((section) => section.id).join(','));
      formData.set('sections_url', window.location.pathname);
      this.cart.setActiveElement?.(event.submitter || document.activeElement);
    }

    this.setSubmitting(true);
    this.announceVariantStatus('Ajout au panier en cours…');

    let itemWasAdded = false;
    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || window.cartStrings?.error || 'Ajout au panier impossible.');
      }

      itemWasAdded = true;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'koota-product', product: payload });
      }

      if (!this.cart || this.cart.renderContents(payload) === false) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }

      this.announceVariantStatus('Produit ajouté au panier.');
    } catch (error) {
      console.error('[Koota product] Add to cart failed', error);
      if (itemWasAdded) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
      this.announceVariantStatus(error.message || window.cartStrings?.error || 'Ajout au panier impossible.');
    } finally {
      this.setSubmitting(false);
    }
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    this.section.classList.toggle('is-cart-loading', isSubmitting);
    this.section.querySelectorAll('[data-koota-add-button], [data-koota-mobile-add-button]').forEach((button) => {
      button.disabled = isSubmitting || !this.currentVariantAvailable;
      button.setAttribute('aria-busy', String(isSubmitting));
    });
  }

  async loadVariant(variantId) {
    if (this.variantRequest) this.variantRequest.abort();

    const controller = new AbortController();
    this.variantRequest = controller;
    this.setVariantLoading(true);
    this.announceVariantStatus(this.messages.variantUpdating);

    try {
      const requestUrl = new URL(this.productUrl, window.location.origin);
      requestUrl.searchParams.set('variant', variantId);
      requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(requestUrl.toString(), {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Variant request failed with status ${response.status}`);

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = parsedDocument.querySelector('[data-koota-product-section]');

      if (!nextSection) throw new Error('Variant section was not found in the response');
      if (controller.signal.aborted) return;

      this.updateVariantContent(nextSection, variantId);
      this.currentVariantId = String(variantId);
      this.updateVariantUrl(variantId);
      this.announceVariantStatus(this.messages.variantUpdated);

      this.section.dispatchEvent(new CustomEvent('koota:variant:change', {
        bubbles: true,
        detail: { variantId: String(variantId), sectionId: this.sectionId },
      }));
    } catch (error) {
      if (error.name === 'AbortError') return;

      if (this.variantSelect) this.variantSelect.value = this.currentVariantId;
      this.announceVariantStatus(this.messages.variantError);
      console.error('[Koota product] Variant update failed', error);
    } finally {
      if (this.variantRequest === controller) {
        this.variantRequest = null;
        this.setVariantLoading(false);
      }
    }
  }

  updateVariantContent(nextSection, variantId) {
    const selectedPurchaseOption = this.section.querySelector('[data-koota-purchase-option]:checked')?.value;
    const selectedPlanId = this.planSelect?.value;

    this.replaceContents('[data-koota-price]', nextSection);
    this.replaceElement('[data-koota-availability]', nextSection);
    this.replaceContents('[data-koota-gallery]', nextSection);
    this.replaceContents('[data-koota-purchase-options-slot]', nextSection);
    this.replaceContents('[data-koota-quantity-tiers]', nextSection);
    this.syncButton('[data-koota-add-button]', nextSection);
    this.syncButton('[data-koota-mobile-add-button]', nextSection);
    this.syncQuantityRules(nextSection);

    this.section.dataset.basePrice = nextSection.dataset.basePrice || '0';
    this.basePrice = Number.parseInt(this.section.dataset.basePrice, 10) || 0;
    this.currentVariantAvailable = !nextSection.querySelector('[data-koota-add-button]')?.disabled;

    if (this.variantSelect) this.variantSelect.value = String(variantId);

    this.mainImage = this.section.querySelector('.koota-product__main-image');
    this.planSelect = this.section.querySelector('[data-koota-selling-plan-select]');
    this.planInput = this.section.querySelector('[data-koota-selling-plan-input]');

    if (selectedPurchaseOption === 'subscription') {
      const subscriptionOption = this.section.querySelector(
        '[data-koota-purchase-option][value="subscription"]'
      );
      if (subscriptionOption) subscriptionOption.checked = true;
    }

    if (selectedPlanId && this.planSelect) {
      const matchingPlan = Array.from(this.planSelect.options).find((option) => option.value === selectedPlanId);
      if (matchingPlan) this.planSelect.value = selectedPlanId;
    }

    this.bindGallery();
    this.bindPurchaseOptions();
    this.bindQuantityTiers();
    this.updateCommerceState();
  }

  replaceContents(selector, nextSection) {
    const currentElement = this.section.querySelector(selector);
    const nextElement = nextSection.querySelector(selector);
    if (!currentElement || !nextElement) return;

    currentElement.innerHTML = nextElement.innerHTML;
  }

  replaceElement(selector, nextSection) {
    const currentElement = this.section.querySelector(selector);
    const nextElement = nextSection.querySelector(selector);
    if (!currentElement || !nextElement) return;

    currentElement.replaceWith(nextElement.cloneNode(true));
  }

  syncButton(selector, nextSection) {
    const currentButton = this.section.querySelector(selector);
    const nextButton = nextSection.querySelector(selector);
    if (!currentButton || !nextButton) return;

    currentButton.innerHTML = nextButton.innerHTML;
    currentButton.disabled = nextButton.disabled;
  }

  syncQuantityRules(nextSection) {
    const nextQuantityInput = nextSection.querySelector('[name="quantity"]');
    if (!this.quantityInput || !nextQuantityInput) return;

    ['min', 'max', 'step'].forEach((attribute) => {
      if (nextQuantityInput.hasAttribute(attribute)) {
        this.quantityInput.setAttribute(attribute, nextQuantityInput.getAttribute(attribute));
      } else {
        this.quantityInput.removeAttribute(attribute);
      }
    });
    this.setQuantity(this.getQuantity());
  }

  setVariantLoading(isLoading) {
    this.section.classList.toggle('is-variant-loading', isLoading);
    this.section.setAttribute('aria-busy', String(isLoading));

    this.section.querySelectorAll('[data-koota-add-button], [data-koota-mobile-add-button]').forEach((button) => {
      button.disabled = isLoading || !this.currentVariantAvailable;
    });
  }

  announceVariantStatus(message) {
    const status = this.section.querySelector('[data-koota-variant-status]');
    if (status) status.textContent = message;
  }

  updateVariantUrl(variantId) {
    if (!window.history?.replaceState || window.Shopify?.designMode) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.delete('section_id');
    window.history.replaceState({ ...window.history.state, variantId }, '', url.toString());
  }

  bindFormatTransitions() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.section.querySelectorAll('[data-koota-format-animation]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const isCurrentFormat = link.getAttribute('aria-current') === 'page';
        const destination = link.href;

        if (reduceMotion.matches) {
          if (isCurrentFormat) event.preventDefault();
          return;
        }

        event.preventDefault();
        if (this.formatTransitionRunning) return;
        this.formatTransitionRunning = true;

        const format = link.dataset.kootaFormatAnimation === 'moulu' ? 'moulu' : 'grains';
        const rect = link.getBoundingClientRect();
        const originX = event.clientX || rect.left + rect.width / 2;
        const originY = event.clientY || rect.top + rect.height / 2;
        const transition = document.createElement('div');
        const particleCount = format === 'moulu' ? 46 : 18;

        transition.className = `koota-format-transition koota-format-transition--${format}`;
        transition.setAttribute('aria-hidden', 'true');

        for (let index = 0; index < particleCount; index += 1) {
          const particle = document.createElement('i');
          const travelX = (Math.random() - 0.5) * (format === 'moulu' ? 280 : 520);
          const travelY = format === 'moulu'
            ? 90 + Math.random() * 300
            : -90 - Math.random() * 330;

          particle.className = 'koota-format-transition__particle';
          particle.style.setProperty('--particle-x', `${originX + (Math.random() - 0.5) * 24}px`);
          particle.style.setProperty('--particle-y', `${originY + (Math.random() - 0.5) * 16}px`);
          particle.style.setProperty('--travel-x', `${travelX}px`);
          particle.style.setProperty('--travel-y', `${travelY}px`);
          particle.style.setProperty('--particle-delay', `${Math.random() * 110}ms`);
          particle.style.setProperty('--particle-rotation', `${(Math.random() - 0.5) * 560}deg`);
          particle.style.setProperty('--particle-scale', `${0.65 + Math.random() * 0.75}`);
          transition.appendChild(particle);
        }

        document.body.appendChild(transition);

        window.setTimeout(() => {
          if (!isCurrentFormat) {
            window.location.assign(destination);
            return;
          }

          transition.remove();
          this.formatTransitionRunning = false;
        }, 720);

        window.setTimeout(() => transition.remove(), 1100);
      });
    });
  }

  bindQuantity() {
    if (!this.quantityInput) return;

    const minus = this.section.querySelector('[data-koota-quantity-minus]');
    const plus = this.section.querySelector('[data-koota-quantity-plus]');

    minus?.addEventListener('click', () => {
      this.setQuantity(this.getQuantity() - 1);
    });

    plus?.addEventListener('click', () => {
      this.setQuantity(this.getQuantity() + 1);
    });

    this.quantityInput.addEventListener('change', () => {
      this.setQuantity(this.getQuantity());
    });
  }

  bindPurchaseOptions() {
    const purchaseOptions = this.section.querySelector('[data-koota-purchase-options]');
    if (!purchaseOptions) return;

    const radios = purchaseOptions.querySelectorAll('[data-koota-purchase-option]');
    const planContainer = purchaseOptions.querySelector('[data-koota-selling-plan]');

    const updatePurchaseOption = () => {
      const selectedOption = purchaseOptions.querySelector('[data-koota-purchase-option]:checked');
      const isSubscription = selectedOption?.value === 'subscription';

      if (planContainer) planContainer.hidden = !isSubscription;
      if (this.planSelect) this.planSelect.disabled = !isSubscription;
      this.syncSellingPlanInput(isSubscription);
      this.updateCommerceState();
    };

    radios.forEach((radio) => radio.addEventListener('change', updatePurchaseOption));
    this.planSelect?.addEventListener('change', () => {
      this.syncSellingPlanInput(true);
      this.updateCommerceState();
    });
    updatePurchaseOption();
  }

  syncSellingPlanInput(isSubscription) {
    if (!this.planInput) return;

    this.planInput.disabled = !isSubscription;
    this.planInput.value = isSubscription ? (this.planSelect?.value || '') : '';
  }

  bindQuantityTiers() {
    this.section.querySelectorAll('[data-koota-quantity-tier]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setQuantity(Number.parseInt(button.dataset.kootaQuantityTier, 10) || 1);
      });
    });
  }

  bindStickyBuy() {
    const stickyBuy = this.section.querySelector('.koota-product__mobile-buy');
    const addButton = this.section.querySelector('[data-koota-add-button]');
    if (!stickyBuy || !addButton || !('IntersectionObserver' in window)) return;

    stickyBuy.setAttribute('aria-hidden', 'true');
    this.stickyBuyObserver?.disconnect();
    this.stickyBuyObserver = new IntersectionObserver(([entry]) => {
      const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      stickyBuy.classList.toggle('is-visible', shouldShow);
      stickyBuy.setAttribute('aria-hidden', String(!shouldShow));
    });
    this.stickyBuyObserver.observe(addButton);
  }

  getQuantity() {
    const minimum = Math.max(1, Number.parseInt(this.quantityInput?.min, 10) || 1);
    const maximum = Number.parseInt(this.quantityInput?.max, 10);
    const increment = Math.max(1, Number.parseInt(this.quantityInput?.step, 10) || 1);
    const requested = Math.max(minimum, Number.parseInt(this.quantityInput?.value, 10) || minimum);
    const stepped = minimum + Math.floor((requested - minimum) / increment) * increment;

    return Number.isFinite(maximum) ? Math.min(stepped, maximum) : stepped;
  }

  setQuantity(quantity) {
    if (!this.quantityInput) return;
    const minimum = Math.max(1, Number.parseInt(this.quantityInput.min, 10) || 1);
    const maximum = Number.parseInt(this.quantityInput.max, 10);
    const increment = Math.max(1, Number.parseInt(this.quantityInput.step, 10) || 1);
    const requested = Math.max(minimum, Number.parseInt(quantity, 10) || minimum);
    const stepped = minimum + Math.floor((requested - minimum) / increment) * increment;

    this.quantityInput.value = Number.isFinite(maximum) ? Math.min(stepped, maximum) : stepped;
    this.updateCommerceState();
  }

  getUnitPrice() {
    const subscriptionOption = this.section.querySelector(
      '[data-koota-purchase-option][value="subscription"]:checked'
    );
    const selectedPlan = this.planSelect?.selectedOptions?.[0];

    if (subscriptionOption && selectedPlan) {
      return Number.parseInt(selectedPlan.dataset.price, 10) || this.basePrice;
    }

    return this.basePrice;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${this.currency}`;
    }
  }

  updateCommerceState() {
    const quantity = this.getQuantity();
    const unitPrice = this.getUnitPrice();
    const totalPrice = unitPrice * quantity;

    this.section.querySelectorAll('[data-koota-button-price]').forEach((element) => {
      element.textContent = this.formatMoney(totalPrice);
    });

    const mobilePrice = this.section.querySelector('[data-koota-mobile-price]');
    if (mobilePrice) mobilePrice.textContent = this.formatMoney(totalPrice);

    this.section.querySelectorAll('[data-koota-quantity-tier]').forEach((button) => {
      const isActive = Number.parseInt(button.dataset.kootaQuantityTier, 10) === quantity;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    const fourPackTotal = this.section.querySelector('[data-koota-tier-total]');
    if (fourPackTotal) fourPackTotal.textContent = this.formatMoney(unitPrice * 4);

    const tierDelivery = this.section.querySelector('[data-koota-tier-delivery]');
    if (tierDelivery) {
      tierDelivery.hidden = !this.freeShippingThreshold || unitPrice * 4 < this.freeShippingThreshold;
    }

    this.updateSubscriptionSummary();
    this.updateShippingProgress(totalPrice);
  }

  updateSubscriptionSummary() {
    const summary = this.section.querySelector('[data-koota-subscription-summary]');
    const selectedPlan = this.planSelect?.selectedOptions?.[0];
    if (!summary || !selectedPlan) return;

    const planPrice = Number.parseInt(selectedPlan.dataset.price, 10) || this.basePrice;
    const saving = Math.max(0, Number.parseInt(selectedPlan.dataset.saving, 10) || 0);
    const discountPercent = Math.max(
      0,
      Number.parseInt(selectedPlan.dataset.discountPercent, 10)
        || (this.basePrice > 0 ? Math.round((saving / this.basePrice) * 100) : 0)
    );
    summary.textContent = this.interpolate(this.messages.subscriptionDelivery, {
      PRICE: this.formatMoney(planPrice),
    });

    if (discountPercent > 0) {
      summary.textContent += ` · −${discountPercent} %`;
    }
  }

  updateShippingProgress(totalPrice) {
    const container = this.section.querySelector('[data-koota-shipping-progress]');
    if (!container || !this.freeShippingThreshold) return;

    const message = container.querySelector('[data-koota-shipping-message]');
    const bar = container.querySelector('[data-koota-shipping-bar]');
    const track = container.querySelector('[role="progressbar"]');
    const remaining = Math.max(0, this.freeShippingThreshold - totalPrice);
    const progress = Math.min(100, Math.round((totalPrice / this.freeShippingThreshold) * 100));

    if (message) {
      message.textContent = remaining === 0
        ? this.messages.shippingUnlocked
        : this.interpolate(this.messages.shippingRemaining, { AMOUNT: this.formatMoney(remaining) });
    }

    if (bar) bar.style.width = `${progress}%`;
    if (track) track.setAttribute('aria-valuenow', String(progress));
  }

  interpolate(template, replacements = {}) {
    return Object.entries(replacements).reduce(
      (message, [key, value]) => message.replaceAll(`__${key}__`, value),
      template
    );
  }
}

function initializeKootaProductSection(section) {
  if (!section || section.dataset.kootaProductInitialized === 'true') return;

  section.dataset.kootaProductInitialized = 'true';
  section.kootaProductSection = new KootaProductSection(section);
}

document.querySelectorAll('[data-koota-product-section]').forEach(initializeKootaProductSection);

document.addEventListener('shopify:section:load', (event) => {
  initializeKootaProductSection(event.target.querySelector('[data-koota-product-section]'));
});
